import type { Balance, BalanceAccount, Network } from "rujira.js";
import { validateAddress } from "rujira.js";
import type { SwapSelectOption } from "rujira.ui";
import type { BanxaSellCapabilities } from "./banxa";
import { banxaNetwork } from "./banxaNetwork";

export const AMOUNT_DECIMALS = 8;

// RUNE on THORChain is a NATIVE asset, not LAYER_1. Both are held directly at the
// wallet address, which is what Banxa's sell flow sends from.
const SELLABLE_TYPES: ReadonlySet<string> = new Set(["LAYER_1", "NATIVE"]);

export type SellSource = {
  id: string;
  crypto: string;
  blockchain: string;
  minimum?: string;
  network: Network;
  account: BalanceAccount;
  option: SwapSelectOption;
};

export const getBanxaSellSources = (
  balances: readonly Pick<Balance, "accounts">[],
  capabilities?: BanxaSellCapabilities
): SellSource[] => {
  if (!capabilities) return [];

  const sources = capabilities.cryptocurrencies.flatMap((cryptocurrency) =>
    cryptocurrency.blockchains.flatMap((blockchain) => {
      const network = banxaNetwork(blockchain.id);
      if (!network) return [];

      return balances.flatMap(({ accounts }) =>
        accounts
          .filter(
            (account) =>
              account.balance > 0n &&
              SELLABLE_TYPES.has(account.asset.type) &&
              account.asset.chain === network &&
              account.asset.metadata.symbol.toUpperCase() ===
                cryptocurrency.id.toUpperCase() &&
              validateAddress(network, account.address)
          )
          .map((account) => {
            const id = `${cryptocurrency.id}:${blockchain.id}:${account.address}`;
            return {
              id,
              crypto: cryptocurrency.id,
              blockchain: blockchain.id,
              minimum: blockchain.minimum,
              network,
              account,
              option: {
                asset: `banxa:${id}`,
                type: account.asset.type,
                chain: account.asset.chain,
                metadata: {
                  symbol: account.asset.metadata.symbol,
                  decimals: AMOUNT_DECIMALS,
                },
              },
            };
          })
      );
    })
  );

  return sources.filter(
    (source, index) =>
      sources.findIndex((candidate) => candidate.id === source.id) === index
  );
};

/**
 * The balance of `network`'s gas token held at `address`, used to check that a
 * non-gas asset can actually be sent.
 */
export const banxaGasBalance = (
  balances: readonly Pick<Balance, "accounts">[],
  network: Network,
  address: string,
  gasSymbol: string
): bigint =>
  balances
    .flatMap(({ accounts }) => accounts)
    .filter(
      (account) =>
        SELLABLE_TYPES.has(account.asset.type) &&
        account.asset.chain === network &&
        account.address === address &&
        account.asset.metadata.symbol.toUpperCase() === gasSymbol.toUpperCase()
    )
    .reduce((total, account) => total + account.balance, 0n);
